import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const phoneNumber = '336-354-1040';
  const phoneLink = 'tel:+13363541040';

  return (
    <footer className="bg-slate-900 text-slate-300 font-body" data-testid="footer">
      {/* Main Footer Content */}
      <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Contact */}
          <div>
            <h3 className="font-heading text-white text-[18px] font-semibold mb-5">Rizzi Law Group</h3>
            <div className="space-y-4 text-[14px]">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 flex-shrink-0 text-slate-400" />
                <div>
                  <p className="font-medium text-white">Winston-Salem</p>
                  <p className="text-slate-400">110 Oakwood Drive, Suite 450</p>
                  <p className="text-slate-400">Winston-Salem, NC 27103</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 flex-shrink-0 text-slate-400" />
                <div>
                  <p className="font-medium text-white">Greenville</p>
                  <p className="text-slate-400">South Carolina</p>
                  <p className="text-slate-500 text-xs mt-1">New Address Coming Soon</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-1 flex-shrink-0 text-slate-400" />
                <a href={phoneLink} className="hover:text-white transition-colors font-medium" data-testid="footer-phone">
                  {phoneNumber}
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-white text-[18px] font-semibold mb-5">Quick Links</h4>
            <ul className="space-y-3 text-[14px]">
              <li>
                <Link to="/" className="hover:text-white transition-colors" data-testid="footer-link-home">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/our-team" className="hover:text-white transition-colors" data-testid="footer-link-team">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/practice-areas" className="hover:text-white transition-colors" data-testid="footer-link-practice">
                  Practice Areas
                </Link>
              </li>
              <li>
                <Link to="/case-results" className="hover:text-white transition-colors" data-testid="footer-link-results">
                  Case Results
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="hover:text-white transition-colors" data-testid="footer-link-testimonials">
                  Testimonials
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-link-contact">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Practice Areas */}
          <div>
            <h4 className="font-heading text-white text-[18px] font-semibold mb-5">Practice Areas</h4>
            <ul className="space-y-3 text-[14px]">
              <li>
                <Link to="/practice-areas/auto-accidents" className="hover:text-white transition-colors">
                  Auto Accidents
                </Link>
              </li>
              <li>
                <Link to="/practice-areas/catastrophic-injury" className="hover:text-white transition-colors">
                  Catastrophic Injury
                </Link>
              </li>
              <li>
                <Link to="/practice-areas/premises-liability" className="hover:text-white transition-colors">
                  Premises Liability
                </Link>
              </li>
              <li>
                <Link to="/practice-areas/trucking-accidents" className="hover:text-white transition-colors">
                  Trucking Accidents
                </Link>
              </li>
              <li>
                <Link to="/practice-areas/workers-compensation" className="hover:text-white transition-colors">
                  Workers' Compensation
                </Link>
              </li>
              <li>
                <Link to="/practice-areas/wrongful-death" className="hover:text-white transition-colors">
                  Wrongful Death
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-white text-[18px] font-semibold mb-5">Legal</h4>
            <div className="space-y-3 text-[14px] leading-relaxed">
              <p className="text-slate-400">Attorney Advertising</p>
              <p className="text-slate-400">No attorney-client relationship is formed by contacting us</p>
              <Link to="/legal" className="inline-block text-white hover:underline font-medium" data-testid="footer-link-legal">
                Full Legal Disclaimers →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8 py-6">
          <p className="text-center text-[13px] text-slate-400">
            &copy; {new Date().getFullYear()} Rizzi Law Group (P.A.). All rights reserved.
          </p>
        </div>
      </div>

      {/* Legal Disclaimers */}
      <div className="border-t border-slate-800 bg-slate-950" data-testid="footer-disclaimers">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <h4 className="text-[13px] font-semibold text-slate-400 uppercase tracking-wider mb-4">Legal Disclaimers</h4>
          <div className="space-y-4 text-[12px] text-slate-500 leading-relaxed">
            <p>
              <strong className="text-slate-400">No Attorney-Client Relationship or Legal Advice</strong> — Communication of information by, in, to or through this Web site and your receipt or use of it (1) is not provided in the course of and does not create or constitute an attorney-client relationship, (2) is not intended as a solicitation, (3) is not intended to convey or constitute legal advice, and (4) is not a substitute for obtaining legal advice from a qualified attorney. You should not act upon any such information without first seeking qualified professional counsel on your specific matter. The hiring of an attorney is an important decision that should not be based solely upon Web site communications or advertisements.
            </p>
            <p>
              The information provided on this website is for informational purposes only.
            </p>
            <p>
              Any cases on this website are illustrative of the matters handled by the firm and do not include all results achieved by the firm. The outcome of a particular case can't be predicated by a lawyer's or firm's past results. Each case is different, and the value of a case can depend on a number of factors, such as: the facts concerning liability in the case, potential liability defenses in the case, economic and non-economic damages in the case, the legal complexity of the case, whether the defendant was insured or self-insured, the available insurance coverage in the case, and whether the case was resolved through negotiation, at trial, or in arbitration.
            </p>
            <p>
              Membership in the Million Dollar Advocates Forum is limited to attorneys who have won million and multi-million-dollar verdicts and settlements. Criteria for membership in Million Dollar Advocates can be found by clicking <a href="https://www.milliondollaradvocates.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 underline hover:text-slate-300">here</a>. Neither Rizzi Law Group, P.A., nor any individually listed attorney of the firm represents that similar results will be achieved in your case. Each case is different and must be evaluated separately.
            </p>
            <p>
              This website constitutes Attorney Advertising.
            </p>
            <p>
              Rizzi Law Group is not responsible for the content of external sites linked from this website.
            </p>
            <p>
              Please note that any electronic communications, including emails and contact forms, might not be secure and are not confidential until an attorney-client relationship is established. For the most secure way to reach us, please give us a call at <a href="tel:+13363541040" className="text-slate-400 underline hover:text-slate-300">(336) 354-1040</a>.
            </p>
            <p>
              Rizzi Law Group is not responsible for errors, omissions, or actions any individual or entity takes based on this website's content. Please reach out to us directly with any questions or concerns.
            </p>
            <p>
              No attorneys listed on this page or responsible for the content of this page represent ourselves as specialists as designated by the Tennessee Commission on Continuing Legal Education and Specialization.
            </p>
            <p>
              This website and its contents are provided "AS IS" without warranty of any kind.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;